import React, { useState, useEffect } from 'react';
import { useSubdomain } from '../contexts/SiteContext';
import { useAuth } from '../contexts/AuthContext';
import { useSiteNavigation } from '../hooks/useSiteNavigation';
import { buildApiUrl, API_ENDPOINTS, authenticatedFetch } from '../config/api';
import SiteForm from './SiteForm';
import LocationForm from './LocationForm';
import LocationList from './LocationList';
import SiteSelector from './SiteSelector';
import SiteList from './SiteList';
import LoadingSpinner from './LoadingSpinner';
import './ManagerDashboard.css';

const ManagerDashboard = () => {
  const { 
    user, 
    hasRole, 
    loading: authLoading 
  } = useAuth();
  
  const { 
    subdomains: sites, 
    activeSubdomain: activeSite, 
    locations,
    loading, 
    error, 
    managerStatus, 
    needsSubdomainCreation: needsSiteCreation,
    changeActiveSubdomain: changeActiveSite,
    loadSubdomains: loadSites,
    loadLocationsBySubdomain: loadLocationsBySite,
    clearError
  } = useSubdomain();

  const { navigateToSiteLocations, navigateToSiteSchedules, navigateToSiteSettings, navigateToSiteMembers } = useSiteNavigation();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [selectedSiteForLocation, setSelectedSiteForLocation] = useState(null);
  const [view, setView] = useState('dashboard'); // 'dashboard', 'sites', 'locations', 'create'
  const [sitesInitialized, setSitesInitialized] = useState(false);
  const [totalMembersCount, setTotalMembersCount] = useState(0);

  // Verificar si es manager
  const isUserManager = hasRole('manager');
  

  // Cargar sites al montar el componente
  useEffect(() => {
    if (isUserManager && !sitesInitialized) {
      setSitesInitialized(true);
      loadSites();
    }
  }, [isUserManager, loadSites, sitesInitialized]);

  // Cargar conteo total de miembros cuando cambien los sites
  useEffect(() => {
    const loadTotalMembersCount = async () => {
      if (sites.length === 0) {
        setTotalMembersCount(0);
        return;
      }

      try {
        let totalCount = 0;
        for (const site of sites) {
          try {
            const apiUrl = buildApiUrl(API_ENDPOINTS.SITE_MEMBERS(site.id));
            const response = await authenticatedFetch(apiUrl);
            if (response.ok) {
              const members = await response.json();
              totalCount += members.length;
            }
          } catch (error) {
            console.warn(`Error loading members for site ${site.id}:`, error);
          }
        }
        setTotalMembersCount(totalCount);
      } catch (error) {
        console.warn('Error loading total members count:', error);
        setTotalMembersCount(0);
      }
    };

    loadTotalMembersCount();
  }, [sites]);

  // Cargar locations cuando se selecciona un site
  useEffect(() => {
    if (activeSite && view === 'locations') {
      loadLocationsBySite(activeSite.id);
    }
  }, [activeSite, view, loadLocationsBySite]);

  // Mostrar formulario de creación si necesita crear un site
  useEffect(() => {
    if (needsSiteCreation && !showCreateForm && !editingSite) {
      setShowCreateForm(true);
      setView('create');
    }
  }, [needsSiteCreation, showCreateForm, editingSite, managerStatus]);

  const handleCreateSite = () => {
    setEditingSite(null);
    setShowCreateForm(true);
    setView('create');
  };


  const handleEditSite = (site) => {
    setEditingSite(site);
    setShowCreateForm(true);
    setView('create');
  };

  const handleFormSuccess = async () => {
    setShowCreateForm(false);
    setEditingSite(null);
    setView('dashboard');
    await loadSites(false, true); // Recargar la lista de sites forzadamente
    // El contexto ya actualiza el managerStatus automáticamente
  };

  const handleFormCancel = () => {
    setShowCreateForm(false);
    setEditingSite(null);
    if (needsSiteCreation) {
      // Si necesita crear un site, mantener en vista de creación
      setView('create');
    } else {
      setView('dashboard');
    }
  };

  const handleSiteSelect = (site) => {
    changeActiveSite(site);
  };

  const handleDeleteSite = async (site) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el centro "${site.name}"? Esta acción no se puede deshacer.`)) {
      try {
        // TODO: Implementar llamada a API para eliminar el site
        console.log('Eliminando site:', site.id);
        // Aquí iría la llamada a la API para eliminar el site
        // await deleteSite(site.id);
        // Recargar la lista de sites
        // loadSites();
      } catch (error) {
        console.error('Error al eliminar el site:', error);
        alert('Error al eliminar el centro de entrenamiento');
      }
    }
  };


  // Funciones para Locations
  const handleCreateLocation = (site = null) => {
    setSelectedSiteForLocation(site || activeSite);
    setEditingLocation(null);
    setShowLocationForm(true);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setSelectedSiteForLocation(sites.find(s => s.id === location.subdomain_id));
    setShowLocationForm(true);
  };

  const handleLocationFormSuccess = () => {
    setShowLocationForm(false);
    setEditingLocation(null);
    setSelectedSiteForLocation(null);
    if (activeSite) {
      loadLocationsBySite(activeSite.id);
    }
  };

  const handleLocationFormCancel = () => {
    setShowLocationForm(false);
    setEditingLocation(null);
    setSelectedSiteForLocation(null);
  };

  // Solo mostrar loading si está cargando la autenticación, cargando sites o no se han inicializado los sites
  if (authLoading || loading || (isUserManager && !sitesInitialized)) {
    return <LoadingSpinner message="Cargando dashboard..." />;
  }

  if (!isUserManager) {
    return (
      <div className="manager-dashboard error">
        <div className="error-message">
          <h2>Acceso Denegado</h2>
          <p>No tienes permisos para acceder a esta sección.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manager-dashboard error">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={clearError} className="btn btn-primary">
              Reintentar
            </button>
            <button onClick={() => window.location.reload()} className="btn btn-success">
              Recargar Página
            </button>
          </div>
          <div className="error-help">
            <p>Si el problema persiste, verifica que:</p>
            <ul>
              <li>Tu conexión a internet esté funcionando</li>
              <li>El servidor backend esté ejecutándose</li>
              <li>Tu sesión no haya expirado</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Si necesita crear un site y no hay formulario mostrado
  if (needsSiteCreation && !showCreateForm) {
    return (
      <div className="manager-dashboard">
        <div className="welcome-message">
          <h1>¡Bienvenido, {user?.name}!</h1>
          <p>Para comenzar a usar el sistema, necesitas crear tu primer centro de entrenamiento.</p>
          <button onClick={handleCreateSite} className="btn btn-primary btn-lg">
            Crear Mi Primer Centro de Entrenamiento
          </button>
        </div>
      </div>
    );
  }

  // Mostrar formulario de creación/edición de Site
  if (showCreateForm) {
    return (
      <div className="manager-dashboard">
        <div className="form-container">
          <SiteForm
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
            editingSite={editingSite}
          />
        </div>
      </div>
    );
  }

  // Mostrar formulario de creación/edición de Location
  if (showLocationForm) {
    return (
      <div className="manager-dashboard">
        <div className="form-container">
          <LocationForm
            onSuccess={handleLocationFormSuccess}
            onCancel={handleLocationFormCancel}
            editingLocation={editingLocation}
            siteId={selectedSiteForLocation?.id}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="manager-dashboard">
      <div className="dashboard-header">
        <h1>Panel de Manager</h1>
        <div className="user-info">
          <span>Hola, {user?.name}</span>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Mensaje de bienvenida cuando no hay sites */}
        {sites.length === 0 && (
          <div className="welcome-section">
            <div className="welcome-card">
              <div className="welcome-icon">🏢</div>
            <h2>¡Bienvenido a tu Panel de Manager!</h2>
            <p className="welcome-description">
              Para comenzar a administrar tu centro de entrenamiento, necesitas crear tu primer site. 
              Un site es donde tus estudiantes practicarán y donde podrás gestionar 
              horarios, instructores y toda la información de tu escuela.
            </p>
              <div className="welcome-features">
                <div className="feature-item">
                  <span className="feature-icon">📅</span>
                  <span>Gestiona horarios de práctica</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">👨‍🏫</span>
                  <span>Administra instructores</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">💰</span>
                  <span>Configura costos y membresías</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📍</span>
                  <span>Define ubicaciones y sucursales</span>
                </div>
              </div>
              <button onClick={handleCreateSite} className="btn btn-primary btn-lg">
                <span className="btn-icon">🚀</span>
                Crear Mi Primer Centro de Entrenamiento
              </button>
            </div>
          </div>
        )}

        {/* Acciones Rápidas */}
        <div className="quick-actions">
          <h2>Acciones Rápidas</h2>
          <div className="action-buttons">
            <button onClick={handleCreateSite} className="btn btn-primary btn-lg">
              <span className="icon">➕</span>
              Crear Nuevo Centro de Entrenamiento
            </button>
          </div>
        </div>

        {/* Vista de Locations */}
        {view === 'locations' && activeSite && (
          <div className="locations-section">
            <LocationList
              siteId={activeSite.id}
              siteName={activeSite.name}
              showCreateButton={true}
            />
          </div>
        )}


        {/* Lista de Sites */}
        {sites.length > 0 && (
          <div className="sites-section">
            <h2>
              <span className="icon">🏢</span>
              Mis Centros de Entrenamiento
            </h2>
            <SiteList
              sites={sites}
              activeSite={activeSite}
              onSiteSelect={handleSiteSelect}
              onEditSite={handleEditSite}
              onViewLocations={(site) => navigateToSiteLocations(site.id)}
              onViewMembers={(site) => navigateToSiteMembers(site.id)}
              onDeleteSite={handleDeleteSite}
              membersCount={totalMembersCount}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default ManagerDashboard;
